import React, { ChangeEvent, KeyboardEvent, useRef, useState } from 'react';
import clsx from 'clsx';
import { MediumHeader } from '../../ui/Header/Header';
import Button from '../../ui/Button/BlockButton';
import TextInput, { SupportedInputType } from '../../ui/TextInput/TextInput';
import TextLink from '../../ui/TextLink/TextLink';
import StarRating from './StarRating/StarRating';
import { useTranslations } from '../../../hooks/useTranslations';
import { useTheme } from '../../../hooks/useTheme';
import styles from './LandscapeCollapsed.module.css';

export interface LandscapeCollapsedProps {
  featureName?: string;
  selectedRating: number;
  onRatingSelect: (index: number) => void;
  submitFeedback?: (feedbackComment: string) => void;
  commentOptional?: boolean;
  enablePanelHeader: boolean;
}

const LandscapeCollapsed = ({
  featureName,
  selectedRating,
  onRatingSelect,
  submitFeedback,
  commentOptional,
  enablePanelHeader,
}: LandscapeCollapsedProps) => {
  const {
    feedbackButtonText = '',
    feedbackButtonTextShort = '',
    feedbackChangeStarRating = '',
    feedbackCommentLabel = '',
    feedbackExperienceHeader = '',
  } = useTranslations();

  const {
    linksNoneDefault: textLinkStyle,
    linksNoneHover: textLinkHoverStyle,
  } = useTheme();

  const [feedbackComment, setFeedbackComment] = useState('');
  const [collapsed, setCollapsed] = useState(true);
  const [interactiveExperienceHeader, setInteractiveExperienceHeader] =
    useState(feedbackExperienceHeader);

  /**
   * In this view of the feedback panel, we show the stars with the rest of the form hidden.
   * Keyboard events on the star radio inputs fire click events as well. We happen to have a
   * click handler on the star radio inputs that will cause the layout to move on to the comment.
   * This means when user uses the arrow keys to select a star rating, if we do nothing else, a click
   * event will fire with the first arrow key and the user will be taken to the comment section. We
   * don't want that. So we set a flag to check if the event was fired from a keydown event and if it
   * was, we bypass the click event. preventDefault on keydown does not work here because that would
   * cancel moving from one radio input to the next with the arrow keys. stopPropagation does not work
   * because that only prevents the event from being consumed by parent events. stomImmediatePropagation
   * does not work because that only prevents successive keydown event handlers defined on the element
   * itself from being called.
   */
  const firedFromKeydown = useRef(false);

  return (
    <>
      <div className={styles.landscapeCollapsedContainer}>
        <div
          className={clsx(
            styles.columnLandscapeCollapsed,
            styles.textAlignLeft,
          )}
          style={{ gridColumn: 'span 5' }}
        >
          <MediumHeader
            as={enablePanelHeader ? 'p' : 'h2'}
            containerCustomStyle={{ lineHeight: '1.5rem' }}
          >
            {interactiveExperienceHeader ?? feedbackExperienceHeader}
          </MediumHeader>
        </div>
        <div
          className={clsx(
            styles.columnLandscapeCollapsed,
            styles.textAlignRight,
          )}
          style={{ gridColumn: 'span 7' }}
        >
          {collapsed ? (
            <div className={styles.startRatingContainer}>
              <StarRating
                // we need 2 different handlers for a11y. keyboard
                // nav events will trigger a click and cause this
                // layout to move on to the comment section when we
                // don't want it to.
                onKeyDown={(
                  e: KeyboardEvent,
                  index: number,
                  selectedText?: string,
                ) => {
                  const keyCode = e.nativeEvent.code;
                  if (
                    keyCode === 'Enter' ||
                    keyCode === 'Space' ||
                    keyCode === 'Tab'
                  ) {
                    onRatingSelect(index);
                    setCollapsed(false);
                    setInteractiveExperienceHeader(
                      selectedText ?? feedbackExperienceHeader,
                    );
                  }
                  firedFromKeydown.current = true;
                }}
                onClick={(index: number, selectedText?: string) => {
                  if (firedFromKeydown.current) {
                    firedFromKeydown.current = false;
                    return;
                  }
                  onRatingSelect(index);
                  setCollapsed(false);
                  setInteractiveExperienceHeader(
                    selectedText ?? feedbackExperienceHeader,
                  );
                }}
                selectedRating={selectedRating}
                featureName={featureName}
                isLandscape
                displayTextFirst
              />
            </div>
          ) : (
            <div data-test="feedbacklLandscapeCollapsedView">
              <div className={styles.landscapeCollapsedFormContainer}>
                <div
                  className={styles.columnLandscapeCollapsed}
                  style={{ gridColumn: 'span 6' }}
                >
                  <TextInput
                    type={SupportedInputType.text}
                    id="feedbackCommentBox"
                    animateLabel
                    label={feedbackCommentLabel || ''}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setFeedbackComment(event.target.value)
                    }
                  />
                </div>
                <div
                  className={styles.columnLandscapeCollapsed}
                  style={{ gridColumn: 'span 4' }}
                >
                  <Button
                    disabled={!commentOptional && !feedbackComment.length}
                    big
                    onClick={() => {
                      if (submitFeedback) submitFeedback(feedbackComment);
                    }}
                  >
                    {feedbackButtonTextShort || feedbackButtonText}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {!collapsed && (
        <div className={styles.columnLandscapeCollapsed}>
          <div
            className={styles.columnChangeStarRating}
            style={{ gridColumn: 'span 12' }}
          >
            <TextLink
              linkStyle={{
                sDefault: textLinkStyle,
                sHover: textLinkHoverStyle,
              }}
              onClick={() => {
                setCollapsed(true);
                setInteractiveExperienceHeader(feedbackExperienceHeader);
              }}
            >
              {feedbackChangeStarRating}
            </TextLink>
          </div>
        </div>
      )}
    </>
  );
};

export default LandscapeCollapsed;
